import { useTranslation } from 'react-i18next'
import type { DiagnosticAssignmentRow } from '@/api/adminDiagnostics'

interface Props {
  assignment: DiagnosticAssignmentRow | undefined
  /** 원생 관리(STUDENT) 권한 보유 여부 — 없으면 상태만 표시 */
  canManage: boolean
  /** 배정 목록 로딩 중 — placeholder만 표시 (미배정 오표시 방지) */
  loading?: boolean
  /** 이 학생의 진단 회차 수 (이력 버튼 노출 게이트) */
  historyCount?: number
  onAssign: () => void
  onGrade: (a: DiagnosticAssignmentRow) => void
  onResult: (a: DiagnosticAssignmentRow) => void
  onReset: (a: DiagnosticAssignmentRow) => void
  onRetake?: (a: DiagnosticAssignmentRow) => void
  onHistory?: (a: DiagnosticAssignmentRow) => void
}

export function DiagnosticCell({
  assignment: a, canManage, loading = false, historyCount = 0,
  onAssign, onGrade, onResult, onReset, onRetake, onHistory,
}: Props) {
  const { t } = useTranslation('admin')

  if (loading) {
    return <span className="text-sm text-gray-300">–</span>
  }

  const levelGraded = Boolean(a?.levelGraded)
  const active = a && a.status !== 'COMPLETED' && a.status !== 'CANCELLED'
  const canReset =
    a && (levelGraded || a.status === 'AIQ_IN_PROGRESS' || a.status === 'LEVEL_GRADING' || a.status === 'COMPLETED')
  const canGrade =
    a && !levelGraded && (a.status === 'ASSIGNED' || a.status === 'AIQ_IN_PROGRESS' || a.status === 'LEVEL_GRADING')
  const canResult = a?.status === 'LEVEL_GRADING' || a?.status === 'COMPLETED' || levelGraded
  const statusLabel = a ? t(`diagnostic.status.${a.status}`) : t('diagnostic.status.none')
  const progress = a && a.status === 'AIQ_IN_PROGRESS' ? ` ${a.aiqSolved}/${a.aiqTotal}` : ''
  const actionBase = 'inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-semibold transition-colors'
  const primaryAction = `${actionBase} border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100`
  const neutralAction = `${actionBase} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`
  const resetAction = `${actionBase} border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100`

  return (
    <div className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="diag-status-badge"
          className={a
            ? 'inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700'
            : 'inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-500'}
        >
          {statusLabel}
          {progress}
        </span>
        {a && a.roundNo > 1 && (
          <span data-testid="diag-round" className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
            {t('diagnostic.history.roundBadge', { round: a.roundNo })}
          </span>
        )}
        {a && levelGraded && a.status !== 'COMPLETED' && (
          <span data-testid="diag-level-graded" className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
            {t('diagnostic.status.levelGraded')}
          </span>
        )}
      </div>
      {canManage && (
        <div data-testid="diag-actions" className="flex flex-wrap items-center gap-2">
          {!active && (
            <button type="button" onClick={onAssign} className={primaryAction}>
              {t('diagnostic.actions.assign')}
            </button>
          )}
          {canGrade && a && (
            <button type="button" onClick={() => onGrade(a)} className={primaryAction}>
              {t('diagnostic.actions.grade')}
            </button>
          )}
          {canResult && a && (
            <button type="button" onClick={() => onResult(a)} className={primaryAction}>
              {t('diagnostic.actions.result')}
            </button>
          )}
          {a?.status === 'COMPLETED' && onRetake && (
            <button data-testid="diag-retake" type="button" onClick={() => onRetake(a)} className={neutralAction}>
              {t('diagnostic.actions.retake')}
            </button>
          )}
          {canReset && a && (
            <button type="button" onClick={() => onReset(a)} className={resetAction}>
              {t('diagnostic.actions.reset')}
            </button>
          )}
          {historyCount > 1 && a && onHistory && (
            <button data-testid="diag-history" type="button" onClick={() => onHistory(a)} className={neutralAction}>
              {t('diagnostic.actions.history')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
